import { Injectable } from '@angular/core';

import { ImageCacheService } from '@core/image-cache/image-cache.service';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LogoService {
  private readonly fallbackLogoLight = '/assets/images/new-cappaccess-logo.png';
  private readonly fallbackLogoDark = '/assets/images/kapnative-white-logo.svg';
  private logoSubjectLight: BehaviorSubject<string | ArrayBuffer> = new BehaviorSubject<string | ArrayBuffer>(
    this.fallbackLogoLight,
  );
  private logoSubjectDark: BehaviorSubject<string | ArrayBuffer> = new BehaviorSubject<string | ArrayBuffer>(
    this.fallbackLogoDark,
  );

  private desiredUrlLight: string | null = null;
  private desiredUrlDark: string | null = null;
  private loadedUrlLight: string | null = null;
  private loadedUrlDark: string | null = null;

  constructor(private imageCache: ImageCacheService) {}

  setLogo(logo: string, mode: 'light' | 'dark'): void {
    if (mode === 'light') {
      this.applyLogo(logo, 'light');
    } else if (mode === 'dark') {
      this.applyLogo(logo, 'dark');
    }
  }

  getLogo(mode: 'light' | 'dark'): Observable<string | ArrayBuffer> {
    if (mode === 'light') return this.logoSubjectLight.asObservable();
    if (mode === 'dark') return this.logoSubjectDark.asObservable();
  }

  private applyLogo(logo: string, mode: 'light' | 'dark'): void {
    const subject = mode === 'light' ? this.logoSubjectLight : this.logoSubjectDark;
    const fallback = mode === 'light' ? this.fallbackLogoLight : this.fallbackLogoDark;

    if (!logo) {
      this.setDesiredUrl(mode, null);
      this.setLoadedUrl(mode, null);
      subject.next(fallback);
      return;
    }

    if (this.getLoadedUrl(mode) === logo) {
      return;
    }

    this.setDesiredUrl(mode, logo);

    // ImageCacheService dedupes in-flight + completed downloads app-wide
    // (partners table, theme logos, uploads, etc.).
    this.imageCache.getImage(logo).subscribe({
      next: (imageData) => {
        if (this.getDesiredUrl(mode) !== logo) {
          return;
        }
        if (imageData) {
          this.setLoadedUrl(mode, logo);
          subject.next(imageData);
        } else {
          this.setLoadedUrl(mode, null);
          subject.next(fallback);
        }
      },
      error: () => {
        if (this.getDesiredUrl(mode) !== logo) {
          return;
        }
        this.setLoadedUrl(mode, null);
        subject.next(fallback);
      },
    });
  }

  private getDesiredUrl(mode: 'light' | 'dark'): string | null {
    return mode === 'light' ? this.desiredUrlLight : this.desiredUrlDark;
  }

  private setDesiredUrl(mode: 'light' | 'dark', url: string | null): void {
    if (mode === 'light') {
      this.desiredUrlLight = url;
    } else {
      this.desiredUrlDark = url;
    }
  }

  private getLoadedUrl(mode: 'light' | 'dark'): string | null {
    return mode === 'light' ? this.loadedUrlLight : this.loadedUrlDark;
  }

  private setLoadedUrl(mode: 'light' | 'dark', url: string | null): void {
    if (mode === 'light') {
      this.loadedUrlLight = url;
    } else {
      this.loadedUrlDark = url;
    }
  }
}
